import type { FeedbackContext, FeedbackDeviceType, FeedbackPlatform } from "../../types";

type Env = {
  userAgent: string;
  maxTouchPoints: number;
  screen?: { width: number; height: number };
  viewport?: { width: number; height: number };
};

const detectPlatform = ({ userAgent: ua, maxTouchPoints }: Env): FeedbackPlatform => {
  if (/android/i.test(ua)) return "android";
  if (/iphone|ipad|ipod/i.test(ua)) return "ios";
  // iPadOS 13+ reports a Macintosh UA; only touch hardware gives it away.
  if (/macintosh/i.test(ua)) return maxTouchPoints > 1 ? "ios" : "macos";
  if (/windows/i.test(ua)) return "windows";
  if (/linux|cros|x11/i.test(ua)) return "linux";
  return "unknown";
};

const detectDeviceType = (env: Env, platform: FeedbackPlatform): FeedbackDeviceType => {
  const ua = env.userAgent;
  if (/ipad|tablet/i.test(ua) || (/android/i.test(ua) && !/mobile/i.test(ua))) return "tablet";
  if (/mobi|iphone|ipod/i.test(ua)) return "mobile";
  if (platform === "ios") return "tablet"; // touch Mac UA with no iPhone token: an iPad
  if (platform === "windows" || platform === "macos" || platform === "linux") return "desktop";
  // UA didn't settle it: fall back to the screen's short side.
  if (env.screen) return Math.min(env.screen.width, env.screen.height) < 768 ? "mobile" : "unknown";
  return "unknown";
};

export const buildFeedbackContext = (env: Env): FeedbackContext => {
  const platform = detectPlatform(env);
  return {
    userAgent: env.userAgent,
    platform,
    deviceType: detectDeviceType(env, platform),
    screen: env.screen,
    viewport: env.viewport,
  };
};

export const readFeedbackContext = (): FeedbackContext =>
  buildFeedbackContext({
    userAgent: navigator.userAgent,
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
    screen: { width: window.screen.width, height: window.screen.height },
    viewport: { width: window.innerWidth, height: window.innerHeight },
  });
